import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  IconButton,
  Divider,
  Card,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  X as CloseIcon,
  Download as DownloadIcon,
  Database,
  CheckCircle2,
  FileText,
  Calendar,
  Wallet,
  Compass,
  BarChart2,
  UserSearch,
  Sparkles,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { fetchAllCollections, exportAllCollectionsToExcel } from '../utils/excelExporter';

const collectionMeta = [
  {
    key: 'velavanFinance',
    name: 'Velavan Finance',
    category: 'Core Ledger',
    icon: FileText,
    color: '#77c8c7',
    desc: 'Customer records, loans, Aadhaar & contact details',
    sheetName: 'Velavan Finance',
  },
  {
    key: 'dueMatters',
    name: 'Due Matters',
    category: 'Collections',
    icon: Calendar,
    color: '#936097',
    desc: 'Weekly due breakdowns, paid & pending dues',
    sheetName: 'Due Matters',
  },
  {
    key: 'interestCalculations',
    name: 'Interest Calculations',
    category: 'Financial Yields',
    icon: Wallet,
    color: '#77c8c7',
    desc: 'Principal in due, interest in due & net profit',
    sheetName: 'Interest Calculations',
  },
  {
    key: 'dailyAccounts',
    name: 'Daily Accounts',
    category: 'Day Bookkeeping',
    icon: Compass,
    color: '#936097',
    desc: 'Daily cash flows, expenses & closing balances',
    sheetName: 'Daily Accounts',
  },
  {
    key: 'finalAccounts',
    name: 'Final Accounts',
    category: 'Monthly & Annual Audit',
    icon: BarChart2,
    color: '#77c8c7',
    desc: 'Consolidated monthly summaries & yearly totals',
    sheetName: 'Final Accounts',
  },
  {
    key: 'accountStatus',
    name: 'Account Status',
    category: 'Portfolio Status',
    icon: UserSearch,
    color: '#936097',
    desc: 'Active, Closed/Paid, and NPA loan classifications',
    sheetName: 'Account Status',
  },
];

const DatabaseExcelExportModal = ({ open, onClose }) => {
  const [exporting, setExporting] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  // Export full multi-sheet workbook
  const handleExportAll = async () => {
    setExporting(true);
    setProgressMsg('Extracting data from all 6 database collections...');
    try {
      await exportAllCollectionsToExcel((progress) => {
        setProgressMsg(progress.message);
      });
      setExportSuccess(true);
      setAlert({
        open: true,
        message: 'Successfully generated and downloaded multi-sheet Excel workbook!',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      setAlert({
        open: true,
        message: error.message || 'Failed to export database collections',
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  // Export a single specific collection sheet
  const handleExportSingleSheet = async (collectionKey, sheetName) => {
    setExporting(true);
    setProgressMsg(`Exporting ${sheetName}...`);
    try {
      const allData = await fetchAllCollections();
      const sheetData = allData[collectionKey] || [];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `${sheetName.replace(/\s+/g, '_')}_${dateStr}.xlsx`);

      setAlert({
        open: true,
        message: `Exported ${sheetName} sheet successfully!`,
        severity: 'success',
      });
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || `Failed to export ${sheetName}`,
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={!exporting ? onClose : undefined}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(226, 232, 240, 0.9)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Top Accent Gradient Bar */}
        <Box sx={{ height: 5, background: 'linear-gradient(90deg, #77c8c7 0%, #936097 100%)' }} />

        <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                bgcolor: 'rgba(119, 200, 199, 0.15)',
                color: '#0f766e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Database size={22} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                  Database Excel Extraction
                </Typography>
                <Chip
                  label="6 Sheets In 1 File"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(147, 96, 151, 0.12)',
                    color: '#936097',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.2 }}>
                Extract and consolidate all database collections into separate sheets in a single Excel workbook (.xlsx)
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={exporting} size="small" sx={{ color: '#94a3b8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.8)' }} />

        <DialogContent sx={{ p: 3 }}>
          {/* Export Progress Bar */}
          {exporting && (
            <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(119, 200, 199, 0.08)', border: '1px solid rgba(119, 200, 199, 0.3)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f766e' }}>
                  {progressMsg}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                  Processing...
                </Typography>
              </Box>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(119, 200, 199, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #77c8c7 0%, #936097 100%)',
                  },
                }}
              />
            </Box>
          )}

          {/* Success Banner */}
          {exportSuccess && !exporting && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(34, 197, 94, 0.08)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CheckCircle2 size={20} color="#15803d" />
              <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 700 }}>
                Excel workbook downloaded successfully with 6 populated tabs! Check your browser downloads.
              </Typography>
            </Box>
          )}

          {/* 6 Collections Grid */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 2, textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.75rem' }}>
            Sheets Included in this Export Workbook:
          </Typography>

          <Grid container spacing={2}>
            {collectionMeta.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Grid item xs={12} sm={6} key={item.key}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 1.8,
                      borderColor: 'rgba(226, 232, 240, 0.9)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: item.color,
                        boxShadow: `0 4px 16px ${item.color}20`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2.5,
                            bgcolor: `${item.color}18`,
                            color: item.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconComponent size={18} />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                              Sheet {index + 1}: {item.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                            {item.desc}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleExportSingleSheet(item.key, item.sheetName)}
                        disabled={exporting}
                        sx={{
                          color: item.color,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': { bgcolor: `${item.color}15` },
                        }}
                      >
                        Single .xlsx
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Features note */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              border: '1px dashed rgba(226, 232, 240, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Sparkles size={18} color="#936097" />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
              <b>Client-side Engine:</b> Direct extraction from MongoDB live collections without overloading backend servers. Fully compatible with Microsoft Excel, Google Sheets & LibreOffice.
            </Typography>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: 'rgba(226, 232, 240, 0.8)' }} />

        <DialogActions sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            disabled={exporting}
            sx={{ color: '#64748b', fontWeight: 600, borderRadius: 2.5 }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            onClick={handleExportAll}
            disabled={exporting}
            startIcon={<DownloadIcon size={18} />}
            sx={{
              background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
              color: '#ffffff',
              borderRadius: 2.5,
              px: 3.5,
              py: 1.1,
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(119, 200, 199, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #66b5b4 0%, #804f84 100%)',
                boxShadow: '0 6px 20px rgba(119, 200, 199, 0.4)',
              },
            }}
          >
            {exporting ? 'Generating Excel...' : 'Export All 6 Collections to Excel (.xlsx)'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%', borderRadius: 3, fontWeight: 600 }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DatabaseExcelExportModal;
